import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import CompetitorSharingModal from './CompetitorSharingModal';

const CompetitorManager = ({ onBack, onCompetitorCreated }) => {
  const { user } = useAuth();
  const [competitors, setCompetitors] = useState([]);
  const [sharedCompetitors, setSharedCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompetitorName, setNewCompetitorName] = useState('');
  const [newCompetitorEmail, setNewCompetitorEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);

  useEffect(() => {
    loadCompetitors();
  }, [user]);

  const loadCompetitors = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      // Load both owned and shared competitors from single endpoint
      const response = await ApiService.getSharedCompetitors(user.user_id);
      
      if (response.success) {
        // The new endpoint returns { competitors: { owned: [...], shared: [...] } }
        setCompetitors(response.competitors?.owned || []);
        setSharedCompetitors(response.competitors?.shared || []);
      } else {
        // Failed to load competitors
        setError('Failed to load competitors');
      }
    } catch (err) {
      // Error loading competitors
      setError('Failed to load competitors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompetitor = async (e) => {
    e.preventDefault();
    
    if (!newCompetitorName.trim()) {
      setCreateError('Competitor name is required');
      return;
    }

    if (newCompetitorName.length > 50) {
      setCreateError('Competitor name must be 50 characters or less');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      
      const response = await ApiService.createCompetitor(user.user_id, newCompetitorName.trim(), newCompetitorEmail.trim());
      
      if (response.success) {
        setNewCompetitorName('');
        setNewCompetitorEmail('');
        setShowCreateForm(false);
        await loadCompetitors(); // Reload the list
        
        if (onCompetitorCreated) {
          onCompetitorCreated(response.competitor);
        }
      } else {
        setCreateError(response.error || 'Failed to create competitor');
      }
    } catch (err) {
      // Error creating competitor
      setCreateError('Failed to create competitor');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewCompetitorName('');
    setNewCompetitorEmail('');
    setCreateError(null);
  };

  const handleShareCompetitor = (competitor) => {
    setSelectedCompetitor(competitor);
    setShowSharingModal(true);
  };

  const handleCloseSharingModal = () => {
    setShowSharingModal(false);
    setSelectedCompetitor(null);
  };

  const handleSharingSuccess = () => {
    // Optionally reload competitors to refresh access counts
    loadCompetitors();
  };

  const handleDeleteCompetitor = async (competitor) => {
    if (!window.confirm(`Are you sure you want to delete "${competitor.name}"? This will permanently remove the competitor and all their practice data.`)) {
      return;
    }

    try {
      setError(null);
      const response = await ApiService.deleteCompetitor(user.user_id, competitor.competitor_id);
      
      if (response.success) {
        // Remove the competitor from the local state
        setCompetitors(competitors.filter(c => c.competitor_id !== competitor.competitor_id));
      } else {
        setError(response.error || 'Failed to delete competitor');
      }
    } catch (err) {
      // Error deleting competitor
      setError('Failed to delete competitor');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading competitors...</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="header">
        <h2 className="card-title">Manage Competitors</h2>
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadCompetitors} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      <div className="content">
        {!showCreateForm ? (
          <div className="create-section">
            <button 
              onClick={() => setShowCreateForm(true)}
              className="primary-button"
            >
              + Create New Competitor
            </button>
          </div>
        ) : (
          <div className="create-form">
            <h3>Create New Competitor</h3>
            <form onSubmit={handleCreateCompetitor}>
              <div className="form-group">
                <label htmlFor="competitorName">Competitor Name:</label>
                <input
                  id="competitorName"
                  type="text"
                  value={newCompetitorName}
                  onChange={(e) => setNewCompetitorName(e.target.value)}
                  placeholder="Enter competitor name"
                  maxLength={50}
                  disabled={creating}
                  autoFocus
                />
                <small className="char-count">
                  {newCompetitorName.length}/50 characters
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="competitorEmail">Email (Optional):</label>
                <input
                  id="competitorEmail"
                  type="email"
                  value={newCompetitorEmail}
                  onChange={(e) => setNewCompetitorEmail(e.target.value)}
                  placeholder="competitor@example.com"
                  maxLength={100}
                  disabled={creating}
                />
                <small className="form-help">
                  If provided, the competitor can log in with Google to practice
                </small>
              </div>

              {createError && (
                <div className="error-message">
                  {createError}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={creating || !newCompetitorName.trim()}
                >
                  {creating ? 'Creating...' : 'Create Competitor'}
                </button>
                <button 
                  type="button" 
                  onClick={handleCancelCreate}
                  className="secondary-button"
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {competitors.length > 0 && (
          <div className="competitors-list">
            <h3>My Competitors</h3>
            <div className="list">
              {competitors.map((competitor) => (
                <div key={competitor.competitor_id} className="competitor-item">
                  <div className="competitor-info">
                    <div className="competitor-avatar">
                      {competitor.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="competitor-details">
                      <h4>{competitor.name}</h4>
                      <p>Created {new Date(competitor.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="competitor-actions">
                    <button
                      onClick={() => handleShareCompetitor(competitor)}
                      className="share-button"
                      title="Share with others"
                    >
                      📤 Share
                    </button>
                    <button
                      onClick={() => handleDeleteCompetitor(competitor)}
                      className="delete-button"
                      title="Delete competitor"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sharedCompetitors.length > 0 && (
          <div className="shared-competitors-list">
            <h3>Shared With Me</h3>
            <div className="list">
              {sharedCompetitors.map((competitor) => (
                <div key={competitor.competitor_id} className="competitor-item shared">
                  <div className="competitor-info">
                    <div className="competitor-avatar shared">
                      {competitor.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="competitor-details">
                      <h4>{competitor.name}</h4>
                      <p>
                        Shared by: {competitor.granted_by || 'Unknown'} • 
                        Access: {competitor.access_level} • 
                        Since: {new Date(competitor.granted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="competitor-badge">
                    <span className={`access-badge ${competitor.access_level}`}>
                      {competitor.access_level}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showSharingModal && selectedCompetitor && (
        <CompetitorSharingModal
          competitor={selectedCompetitor}
          onClose={handleCloseSharingModal}
          onSuccess={handleSharingSuccess}
        />
      )}

      <style jsx>{`
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .back-button {
          background: #6c757d;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .back-button:hover {
          background: #545b62;
        }

        .error-banner {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .retry-button {
          background: #dc3545;
          color: white;
          border: none;
          padding: 4px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
        }

        .create-section {
          margin-bottom: 30px;
        }

        .create-form {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }

        .create-form h3 {
          margin: 0 0 20px 0;
          color: #333;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #333;
        }

        .form-group input {
          width: 100%;
          max-width: 300px;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0,123,255,.25);
        }

        .char-count {
          display: block;
          margin-top: 5px;
          color: #666;
          font-size: 0.85rem;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 8px 12px;
          border-radius: 4px;
          margin-bottom: 15px;
          font-size: 0.9rem;
        }

        .form-actions {
          display: flex;
          gap: 10px;
        }

        .primary-button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.2s;
        }

        .primary-button:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .primary-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        .secondary-button {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.2s;
        }

        .secondary-button:hover:not(:disabled) {
          background-color: #545b62;
        }

        .secondary-button:disabled {
          background-color: #adb5bd;
          cursor: not-allowed;
        }

        .competitors-list h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .competitor-item {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 15px;
        }

        .competitor-info {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .competitor-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #007bff;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
        }

        .competitor-details h4 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .competitor-details p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .competitor-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .share-button {
          background: #28a745;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: background-color 0.2s;
        }

        .share-button:hover {
          background: #218838;
        }

        .shared-competitors-list {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e9ecef;
        }

        .shared-competitors-list h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .competitor-item.shared {
          border-left: 4px solid #17a2b8;
          background: #f8f9fa;
        }

        .competitor-avatar.shared {
          background: #17a2b8;
        }

        .competitor-badge {
          display: flex;
          align-items: center;
        }

        .access-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .access-badge.owner {
          background: #d4edda;
          color: #155724;
        }

        .access-badge.manager {
          background: #d1ecf1;
          color: #0c5460;
        }

        .access-badge.viewer {
          background: #f8d7da;
          color: #721c24;
        }
      `}</style>
    </div>
  );
};

export default CompetitorManager;